/**
 * `/email-templates` — overview of the six transactional-email slots across four locales.
 * Each cell links into the draft editor for that `(slot, locale)`.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Alert,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { emailTemplatesApi } from '@/api/tenant/emailTemplates';
import { isApiError } from '@/api/errors';
import { EMAIL_LOCALES, EMAIL_TEMPLATE_NAMES } from '@/api/types';
import type {
  EmailLocale,
  EmailTemplateCell,
  EmailTemplateName,
  EmailTemplateStatus,
} from '@/api/types';
import { useCopy } from '@/hooks/useCopy';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: EmailTemplateCell[] }
  | { kind: 'suspended' }
  | { kind: 'error' };

export function EmailTemplates() {
  const { t } = useCopy();
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });

  const loadRows = useCallback(async () => {
    try {
      setLoad({ kind: 'ready', rows: await emailTemplatesApi.list() });
    } catch (err) {
      setLoad({ kind: isApiError(err) && err.status === 403 ? 'suspended' : 'error' });
    }
  }, []);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      await loadRows();
    })();
  }, [loadRows]);

  function retry() {
    setLoad({ kind: 'loading' });
    void loadRows();
  }

  if (load.kind === 'loading') {
    return (
      <Box role="status" sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress aria-label={t('app.loading', 'Loading')} />
      </Box>
    );
  }

  if (load.kind === 'suspended') {
    return (
      <Notice
        title={t('emailTemplates.suspended.title', 'Email templates are paused for your tenant')}
        body={t(
          'emailTemplates.suspended.body',
          'Your tenant is not active, so email templates cannot be changed right now. Contact support to reactivate it.',
        )}
      />
    );
  }

  if (load.kind === 'error') {
    return (
      <Notice
        title={t('emailTemplates.loadError.title', "We couldn't load your email templates")}
        body={t(
          'emailTemplates.loadError.body',
          'Something went wrong loading your email template grid. Try again.',
        )}
        action={
          <Button variant="contained" onClick={retry}>
            {t('common.retry', 'Retry')}
          </Button>
        }
      />
    );
  }

  const byCell = new Map(
    load.rows.map((cell) => [`${cell.templateName}:${cell.locale}`, cell] as const),
  );
  const cellFor = (slot: EmailTemplateName, locale: EmailLocale) =>
    byCell.get(`${slot}:${locale}`) ?? emailTemplateFallback(slot, locale);
  const allDefaultFromFallback = load.rows.length === 0;

  return (
    <Box>
      <Typography variant="h1" gutterBottom>
        {t('emailTemplates.title', 'Email templates')}
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 3, maxWidth: 780 }}>
        {t(
          'emailTemplates.intro',
          'Customize transactional email subjects and HTML bodies per slot and locale. Drafts stay private until a later publish step.',
        )}
      </Typography>
      {allDefaultFromFallback ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'emailTemplates.empty',
            'No tenant template statuses were returned, so every slot is shown as using the platform default.',
          )}
        </Alert>
      ) : null}

      <TableContainer component={Paper} variant="outlined">
        <Table sx={{ minWidth: 760 }}>
          <Box
            component="caption"
            sx={{
              captionSide: 'bottom',
              p: 1.5,
              textAlign: 'left',
              color: 'text.secondary',
              typography: 'caption',
            }}
          >
            {t(
              'emailTemplates.tableCaption',
              'Six email slots by four locales. Open a cell to view or edit its draft.',
            )}
          </Box>
          <TableHead>
            <TableRow>
              <TableCell>{t('emailTemplates.columns.slot', 'Email')}</TableCell>
              {EMAIL_LOCALES.map((locale) => (
                <TableCell key={locale} align="center">
                  {t(`emailTemplates.locales.${locale}`, locale.toUpperCase())}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {EMAIL_TEMPLATE_NAMES.map((slot) => (
              <TableRow key={slot}>
                <TableCell component="th" scope="row">
                  <Typography sx={{ fontWeight: 600 }}>{slotLabel(t, slot)}</Typography>
                </TableCell>
                {EMAIL_LOCALES.map((locale) => (
                  <TableCell key={locale} align="center">
                    <TemplateCellLink cell={cellFor(slot, locale)} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function TemplateCellLink({ cell }: { cell: EmailTemplateCell }) {
  const { t } = useCopy();
  const slot = slotLabel(t, cell.templateName);
  const locale = t(`emailTemplates.locales.${cell.locale}`, cell.locale.toUpperCase());
  return (
    <Button
      component={RouterLink}
      to={`/email-templates/${cell.templateName}/${cell.locale}`}
      variant="text"
      sx={{
        width: 150,
        minHeight: 72,
        display: 'inline-flex',
        flexDirection: 'column',
        gap: 0.75,
        alignItems: 'center',
      }}
      aria-label={t('emailTemplates.openCell', 'Open {{slot}} {{locale}}', { slot, locale })}
    >
      <StatusBadge status={cell.status} />
      {cell.hasDraft && cell.status !== 'DRAFT' ? (
        <Chip size="small" color="warning" label={t('emailTemplates.hasDraft', 'Has draft')} />
      ) : null}
    </Button>
  );
}

function StatusBadge({ status }: { status: EmailTemplateStatus }) {
  const { t } = useCopy();
  const color = status === 'PUBLISHED' ? 'success' : status === 'DRAFT' ? 'warning' : 'default';
  return (
    <Chip
      size="small"
      color={color}
      label={t(`emailTemplates.status.${status}`, status)}
      aria-label={t(`emailTemplates.status.${status}`, status)}
    />
  );
}

function slotLabel(
  t: (key: string, fallback?: string, values?: Record<string, string | number>) => string,
  slot: EmailTemplateName,
): string {
  return t(`emailTemplates.names.${slot}`, slot);
}

function emailTemplateFallback(
  templateName: EmailTemplateName,
  locale: EmailLocale,
): EmailTemplateCell {
  return {
    templateName,
    locale,
    status: 'DEFAULT',
    hasDraft: false,
    hasPublished: false,
    updatedAt: null,
  };
}

function Notice({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <Paper sx={{ p: 4, maxWidth: 640 }} elevation={1}>
      <Typography variant="h1" component="h1" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {body}
      </Typography>
      {action ? <Box sx={{ mt: 3 }}>{action}</Box> : null}
    </Paper>
  );
}
